import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle, CheckCircle2 } from 'lucide-react';
import { branches } from '../data/mock';

export default function Contact() {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    // Save locally for now
    try {
      const saved = JSON.parse(localStorage.getItem('bodyline_leads') || '[]');
      saved.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem('bodyline_leads', JSON.stringify(saved));
    } catch (_) {}
    setSent(true);
    setTimeout(() => {
      setSent(false);
      setForm({ name: '', phone: '', message: '' });
    }, 3000);
  };

  return (
    <section id="contact" className="relative bg-white py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <p className="section-eyebrow text-blue-600">07 — Contact</p>
          <h2 className="mt-4 font-anton text-3xl sm:text-4xl md:text-6xl leading-[0.95] text-slate-900">
            LET'S <span className="text-blue-600">TALK</span>
          </h2>
          <p className="mt-4 text-slate-600 max-w-md">
            Have a question, want a free trial, or need help choosing a plan? Reach out — we usually respond within an hour.
          </p>

          <div className="mt-8 space-y-4">
            <a href="tel:+918482031021" className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 hover:bg-blue-50 ring-1 ring-slate-200 hover:ring-blue-300 transition">
              <span className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center">
                <Phone className="w-4 h-4" />
              </span>
              <div>
                <p className="text-xs text-slate-500">Phone</p>
                <p className="text-slate-900 font-semibold">+91 84820 31021</p>
              </div>
            </a>
            <a href="https://wa.me/918482031021" target="_blank" rel="noreferrer" className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 hover:bg-emerald-50 ring-1 ring-slate-200 hover:ring-emerald-300 transition">
              <span className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
                <MessageCircle className="w-4 h-4" />
              </span>
              <div>
                <p className="text-xs text-slate-500">WhatsApp</p>
                <p className="text-slate-900 font-semibold">Chat with us</p>
              </div>
            </a>
            <a href="mailto:contact@bodylinegym.in" className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 hover:bg-amber-50 ring-1 ring-slate-200 hover:ring-amber-300 transition">
              <span className="w-11 h-11 rounded-xl bg-amber-500 text-slate-950 flex items-center justify-center">
                <Mail className="w-4 h-4" />
              </span>
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="text-slate-900 font-semibold">contact@bodylinegym.in</p>
              </div>
            </a>

            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              {branches.map((b) => (
                <div key={b.name} className="p-4 rounded-2xl bg-white ring-1 ring-slate-200">
                  <p className="text-xs text-blue-600 font-bold">{b.tag}</p>
                  <p className="mt-1 font-anton text-lg text-slate-900">{b.name}</p>
                  <p className="mt-1 text-xs text-slate-500 flex items-start gap-1">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 text-blue-600" />
                    {b.address}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="relative rounded-3xl p-8 md:p-10 bg-slate-950 text-white shadow-2xl">
          <h3 className="font-anton text-3xl md:text-4xl">SEND A MESSAGE</h3>
          <p className="mt-2 text-white/60 text-sm">Fill the form and we'll get back to you shortly.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs text-white/60 font-medium">Your Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                type="text"
                placeholder="John Doe"
                className="mt-1 w-full bg-white/5 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl px-4 py-3 text-white placeholder:text-white/30 outline-none transition"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 font-medium">Phone Number</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
                type="tel"
                placeholder="+91 84820 31021"
                className="mt-1 w-full bg-white/5 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl px-4 py-3 text-white placeholder:text-white/30 outline-none transition"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 font-medium">Message</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={4}
                placeholder="I'd like to book a free trial..."
                className="mt-1 w-full bg-white/5 border border-white/10 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl px-4 py-3 text-white placeholder:text-white/30 outline-none transition resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={sent}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-emerald-500 text-white font-semibold py-3.5 rounded-full transition-all shadow-lg shadow-blue-600/30"
            >
              {sent ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Message Sent!
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Message
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
